<template>
  <div class="bg-white report pb-12">
    <div v-if="!topPage" class="fixed bottom-4 right-4 cursor-pointer hidden md:block" @click.prevent="scrollToTop">
      <ArrowUpCircleIcon
        class="h-5 md:h-7 lg:h-7 w-5 md:w-7 lg:w-7 inline-block text-pdsPrimaryColor cursor-pointer"
        aria-hidden="true"
      />
    </div>
    <div class="text-white font-bold p-0 bg-primary-default">
      <div class="ml-auto w-11/12">
        <div class="grid grid-rows md:grid-cols md:grid-rows-1 md:grid-cols-2">
          <div class="py-4">
            <!-- Back Link -->
            <div class="text-sm mb-1 cursor-pointer -ml-1">
              <router-link to="/">
                <ChevronLeftIcon class="h-5 w-5 inline-block align-text-top" aria-hidden="true" />
                <span class="align-middle">Patiënt zoeken</span>
              </router-link>
            </div>
            <h1 class="text-2xl font-bold tracking-tight text-white m-0 pl-1">Rapportage</h1>
          </div>
        </div>
      </div>
    </div>
    <section class="pt-6 mx-auto w-10/12 min-h-screen text-pdsPrimaryColor">
      <div class="grid grid-rows">
        <div class="relative mb-4 w-full md:w-1/2">
          <div class="text-pdsPrimaryColor text-base font-bold mb-3">Zoeken</div>
          <v-input-group-simple-2
            id="reportSearch"
            :value="searchDetails.searchText"
            v-model="searchDetails.searchText"
            :overwrite-value="searchDetails.searchText"
            :rules="{
              required: false
            }"
            placeholder="Zoeken op naam, geboortedatum of BSN"
            @keyup="onKeyUp"
          />
          <sync-loader v-if="loading" color="#73c8d2" size="8px" class="absolute right-4 top-12" />
          <MagnifyingGlassIcon v-else class="w-5 h-5 text-pdsPrimaryColor ml-2 absolute right-4 top-12" />
        </div>
      </div>
      <div class="flex flex-col md:flex-row gap-4 mb-4">
        <div class="">
          <label class="font-bold">Therapie</label>
          <v-select-group
            class="-mt-2"
            v-model="searchDetails.therapy"
            :overwrite-value="searchDetails.therapy"
            :initselected="searchDetails.therapy"
            selectid="thearapyId"
            @change="changeSearch($event, 'therapy')"
          >
            <option value="Toon Alles">Toon Alles</option>
            <option
              v-for="(item, index) in therapies"
              :key="'therapy' + index"
              :data-index="'therapy' + index"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </v-select-group>
        </div>
        <div class="">
          <label class="font-bold">Machtigingsstatus</label>
          <v-select-group
            class="-mt-2"
            v-model="searchDetails.maintenanceStatus"
            :overwrite-value="searchDetails.maintenanceStatus"
            :initselected="searchDetails.maintenanceStatus"
            selectid="maintenanceStatusId"
            @change="changeSearch($event, 'maintenanceStatus')"
          >
            <option value="Toon Alles">Toon Alles</option>
            <option value="NotExpired">Actief</option>
            <option value="Expired">Inactief</option>
            <option value="AlmostExpired">Bijna verlopen</option>
          </v-select-group>
        </div>
        <div class="">
          <label class="font-bold">Verzekeraar</label>
          <v-select-group
            class="-mt-2"
            v-model="searchDetails.payer"
            :overwrite-value="searchDetails.payer"
            :initselected="searchDetails.payer"
            selectid="payerId"
            @change="changeSearch($event, 'payer')"
          >
            <option value="Toon Alles">Toon Alles</option>
            <option
              v-for="(item, index) in payers"
              :key="'payer' + index"
              :data-index="'payer' + index"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </v-select-group>
        </div>
        <div class="">
          <label class="font-bold">Startdatum</label>
          <date-picker
            range
            class="-mt-2"
            id="startDate"
            ref="startDate"
            type="date"
            name="searchDetailsStartDate"
            @selected="handleDate"
            v-model="searchDetails.startDate"
          />
        </div>
        <div class="">
          <label class="font-bold">Einddatum</label>
          <date-picker
            range
            class="-mt-2"
            id="endDate"
            ref="endDate"
            type="date"
            name="searchDetailsEndDate"
            @selected="handleDate"
            v-model="searchDetails.endDate"
          />
        </div>
        <button
          @click.prevent="resetFilters"
          class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white border border-primary-default bg-primary-default rounded-full hover:bg-primary-default/50 items-center mb-2 h-fit mt-2 md:mt-11 w-fit"
        >
          <ArrowPathIcon class="h-4 md:h-5 lg:h-5 w-4 md:w-5 lg:w-5 inline-block text-white mr-2" aria-hidden="true" />
          <span class="">Reset filters</span>
        </button>
      </div>
      <div>
        <tables v-if="activeLicenses.length !== 0">
          <template #tableHeaders>
            <thead>
              <tr class="bg-sky-50">
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                  v-for="header in tableHeaders"
                  :key="header"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
          </template>
          <template #tableBody>
            <tbody class="bg-white divide-y divide-sky-200">
              <tr v-for="(row, index) in activeLicenses" ref="row" :key="index" :index="index">
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  <a href="#" class="text-black no-underline" @click="selectPatient(row.customerId)">
                    {{ row.customerName }}
                  </a>
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  {{ $formatDate(row.dob) }}
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  {{ row.bsn }}
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  {{ row.therapyName }}
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  <div>
                    <div
                      class="rounded-xl h-4 w-4 inline-block mr-1 -mb-1"
                      :class="getStatusColor(row.expirationStatus)"
                    ></div>
                    {{ getComputedStatus(row.expirationStatus) }}
                  </div>
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  {{ row.payer }}
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black">
                  {{ $formatDate(row.startDate) }}
                </td>
                <td class="py-3.5 pl-4 pr-3 text-left text-base text-black" v-if="row.endDate !== null">
                  {{ $formatDate(row.endDate) }}
                </td>
                <td />
              </tr>
            </tbody>
          </template>
        </tables>
        <Alert
          class="my-4"
          :html="true"
          htmlMessage="Er werden geen gegevens gevonden."
          type="info"
          v-if="activeLicenses.length === 0 && !noMoreData && !loading"
        ></Alert>
        <div class="flex justify-center pt-4">
          <LoadMoreIndicator v-if="loading" message="Resultaten worden geladen" />
          <div v-if="noMoreData">Geen records meer</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ArrowPathIcon, ArrowUpCircleIcon, ChevronLeftIcon } from '@heroicons/vue/20/solid';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import SyncLoader from 'vue-spinner/src/SyncLoader.vue';
import { getAllPayers } from '@/services/crm/getAllPayers';
import { getTherapiesByActivePortal } from '@/services/accountabilities/getTherapiesByActivePortal';
import { mapActions, useStore } from 'vuex';
import { ref, onBeforeMount, reactive, watch, onMounted } from 'vue';
import router from '@/router';
import { from } from 'rxjs';
import { fetchQuery } from '@/services/messaging/fetchQuery';
import { activePpwRecId$ } from '@/services';

export default {
  name: 'Report',
  components: {
    SyncLoader,
    MagnifyingGlassIcon,
    ArrowUpCircleIcon,
    ArrowPathIcon,
    ChevronLeftIcon
  },
  setup() {
    const tableHeaders = [
      'A-Z',
      'Geboortedatum',
      'BSN',
      'Therapie',
      'Machtigingstatus',
      'Verzekeraar',
      'Startdatum',
      'Einddatum'
    ];
    const store = useStore();
    const activeLicenses = ref([]);
    const therapies = ref(null);
    const payers = ref(null);
    const searchDetails = reactive({
      searchText: null,
      therapy: 'Toon Alles',
      maintenanceStatus: 'Toon Alles',
      payer: 'Toon Alles',
      startDate: null,
      endDate: null
    });
    const sortFieldDetails = ref(null);
    const pagedSortedFilteredLicenses = ref([]);
    const page = ref(1);
    const loading = ref(false);
    const noMoreData = ref(false);
    const pageSize = 20;
    const activePpwRecId = ref(null);
    const bottom = ref(false);
    const topPage = ref(false);
    const typingTimer = ref(null);

    activePpwRecId$.subscribe((value) => {
      activePpwRecId.value = value;
    });
    const setActivePatient = (value) => store.dispatch('patient/setActivePatient', value);
    const setEndpoints = (value) => store.dispatch('patient/setEndpoints', value);
    const fetchSubscriptions = () => {
      from(getTherapiesByActivePortal()).subscribe((value) => (therapies.value = value));
      from(getAllPayers(true)).subscribe((value) => (payers.value = value));
    };
    const scrollToTop = () => {
      window.scrollTo(0, 0);
    };
    const fetchLicenses = async () => {
      if (loading.value || noMoreData.value) return;
      loading.value = true;
      if (page.value === 1) activeLicenses.value = [];
      const response = await fetchQuery('GetLicensesByCurrentDepartmentWithPaging', {
        PpwRecId: activePpwRecId.value,
        PageNumber: page.value,
        PageSize: pageSize,
        IsPaging: true,
        TherapyId: searchDetails.therapy !== 'Toon Alles' ? searchDetails.therapy : null,
        ExpirationStatus: searchDetails.maintenanceStatus !== 'Toon Alles' ? searchDetails.maintenanceStatus : null,
        PayerId: searchDetails.payer !== 'Toon Alles' ? searchDetails.payer : null,
        StartDate: Array.isArray(searchDetails.startDate) ? searchDetails.startDate.filter(Boolean).join('|') : null,
        EndDate: Array.isArray(searchDetails.endDate) ? searchDetails.endDate.filter(Boolean).join('|') : null,
        SearchText: searchDetails.searchText
      });

      if (response) {
        page.value++;

        if (response.licenses && response.licenses.length == 0) {
          loading.value = false;
          return;
        }
        activeLicenses.value.push(...response.licenses);
        loading.value = false;
        if (response.totalRecords <= page.value * pageSize) {
          noMoreData.value = true;
        }
      } else {
        loading.value = false;
      }
    };
    const changeSearch = (event, key) => {
      searchDetails[key] = event.target.value;
      page.value = 1;
      noMoreData.value = false;
      fetchLicenses();
    };
    const onKeyUp = () => {
      clearTimeout(typingTimer.value);
      page.value = 1;
      noMoreData.value = false;
      typingTimer.value = setTimeout(() => fetchLicenses(), 600);
    };
    const resetFilters = () => {
      page.value = 1;
      noMoreData.value = false;
      for (let key in searchDetails) {
        if (['therapy', 'maintenanceStatus', 'payer'].includes(key)) {
          searchDetails[key] = 'Toon Alles';
        } else {
          searchDetails[key] = null;
        }
      }
      fetchLicenses();
    };
    const handleDate = (key, selectedDate) => {
      page.value = 1;
      noMoreData.value = false;
      if (Object.keys(searchDetails).includes(key)) {
        // If the key exists, update its value
        searchDetails[key] = selectedDate;
        fetchLicenses();
      } else {
        // If the key doesn't exist, log an error or handle it accordingly
        console.error(`Key '${key}' does not exist in searchDetails.`);
      }
    };
    const topVisible = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      return scrollY === 0;
    };
    const bottomVisible = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const visible = document.documentElement.clientHeight;
      const pageHeight = document.documentElement.scrollHeight;
      const bottomOfPage = Math.ceil(visible + scrollY) >= Math.ceil(pageHeight);
      return bottomOfPage;
    };
    const getComputedStatus = (status) => {
      return status === 'NotExpired' ? 'Actief' : status === 'Expired' ? 'Inactief' : 'Bijna verlopen';
    };
    const getStatusColor = (status) => {
      return status === 'NotExpired' ? 'bg-green-500' : status === 'Expired' ? 'bg-red-500' : 'bg-orange-500';
    };
    const selectPatient = (PatientId) => {
      setActivePatient(PatientId).then((result) => {
        if (result.data.IsActiveCustomer) {
          router.push('/patientdashboard');
        } else {
          this.setEndpoints('edit');
          router.push('/patientgegevens');
        }
      });
    };

    watch(
      () => bottom.value,
      (newVal) => {
        if (newVal) {
          fetchLicenses();
        }
      }
    );

    onBeforeMount(() => {
      fetchSubscriptions();
    });
    onMounted(() => {
      window.addEventListener('scroll', () => {
        bottom.value = bottomVisible();
        topPage.value = topVisible();
      });
      fetchLicenses();
    });

    return {
      setEndpoints,
      setActivePatient,
      ...mapActions('patient', ['setActivePatient']),
      activeLicenses,
      therapies,
      payers,
      searchDetails,
      changeSearch,
      pagedSortedFilteredLicenses,
      sortFieldDetails,
      resetFilters,
      handleDate,
      loading,
      noMoreData,
      scrollToTop,
      typingTimer,
      onKeyUp,
      tableHeaders,
      getComputedStatus,
      getStatusColor,
      selectPatient,
      topPage
    };
  }
};
</script>
<style lang="scss" scoped>
.report {
  :deep(.vuejs3-datepicker) {
    padding-top: 18px;
  }
  :deep(.datepicker .vuejs3-datepicker__value) {
    height: 50px;
    border: 1px solid #dddddd;
    border-radius: 3px;
  }
}
</style>
